import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import NavBar from './components/NavBar';
import ProtectedRoute from './components/ProtectedRoute';
import DocumentDeepLink from './components/DocumentDeepLink';
import HelpOrb from './components/help/HelpOrb';
import Login from './pages/Login';
import RelinkUploader from './pages/RelinkUploader';
import Cases from './pages/Cases';
import CaseDetail from './pages/CaseDetail';
import DocumentWindow from './pages/DocumentWindow';
import OrgSettings from './pages/OrgSettings';
import OpsAdminShell from './pages/ops_admin/OpsAdminShell';
import Help from './pages/Help';
import HelpDoc from './pages/HelpDoc';
import SentencingResearch from './pages/SentencingResearch';
import { NotepadProvider } from './contexts/NotepadContext';
import NotepadDrawer, { NotepadFab } from './components/NotepadDrawer';
import SelectionClipper from './components/SelectionClipper';
import { ToastProvider } from './components/jobs/ToastProvider';
import { BackgroundJobsProvider } from './components/jobs/BackgroundJobsContext';
import { DownloadsHost } from './components/inventory/DownloadsPanel';

// One case-scoped notepad provider wrapping BOTH the routes and the app-level
// DocumentDeepLink viewer. This is why clip overlays render whether a doc is
// opened from a case tab OR via a deep-link / "Open source" (both viewers sit
// inside this single provider), and why an overlay click opens the same drawer.
// caseId comes from the URL; keying on it gives each case a clean instance.
function NotepadHost({ children }) {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/case\/([^/]+)/);
  const caseId = m ? m[1] : null;
  return (
    <NotepadProvider key={caseId || 'none'} caseId={caseId}>
      {/* One case-wide background-jobs poller covering BOTH the case tabs and
          the app-level deep-link viewer (RedactionStudio lives there). Keyed by
          caseId so each case gets a clean poller + transition state. */}
      <BackgroundJobsProvider key={`bgjobs-${caseId || 'none'}`} caseId={caseId}>
        {children}
        {caseId && (
          <>
            <NotepadFab />
            <NotepadDrawer />
            <SelectionClipper />
            <DownloadsHost caseId={caseId} />
          </>
        )}
      </BackgroundJobsProvider>
    </NotepadProvider>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <div className="app">
        <NavBar />
        <NotepadHost>
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/re-link-uploader" element={<RelinkUploader />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/cases" element={<Cases />} />
              <Route path="/case/:caseId" element={<CaseDetail />} />
              {/* A document in its own browser window — the viewer's "Open in a
                  new window" button; sits beside the list or on a second screen. */}
              <Route path="/case/:caseId/view/:docId" element={<DocumentWindow />} />
              <Route path="/case/:caseId/:tab" element={<CaseDetail />} />
              <Route path="/settings" element={<OrgSettings />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/:slug" element={<HelpDoc />} />
              {/* Sentencing Research — entitlement-gated (features.sentencing_tools) */}
              <Route path="/sentencing" element={<SentencingResearch />} />
              {/* Ops Admin — all sub-routes handled inside OpsAdminShell */}
              <Route path="/ops_admin/*" element={<OpsAdminShell />} />
            </Route>
            <Route path="*" element={<Navigate to="/cases" replace />} />
          </Routes>
        </main>
        {/* App-wide handler for `?doc=<id>` deep links (Ask Arc citations,
            "Open source", external share URLs). Inside NotepadHost so the
            viewer it opens has the notepad context (clip overlays render). */}
        <DocumentDeepLink />
      </NotepadHost>
        {/* Floating help orb. Suppresses itself on /login and /help routes. */}
        <HelpOrb />
      </div>
    </ToastProvider>
  );
}
